/**
 * CollaborateSection · home-page money-making replacement for "Sponsors"
 * ---------------------------------------------------------------------------
 * 3 revenue tracks presented as premium tiles:
 *   1. Affiliate       — 15% commission for 90 days on referred customers
 *   2. Reseller        — wholesale training & audit pricing + white-label
 *   3. Case-Sharing    — freelance analysts earn 40-60% on consultation ops
 *
 * Every "APPLY" button opens the same modal which routes to `/api/collab/apply`.
 */
import React, { useState } from 'react';
import { Users, Zap, ShieldCheck, ArrowRight, X, Send, Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const TRACKS = [
  {
    id: 'affiliate',
    label: 'Affiliate Program',
    tagline: 'Creators · YouTubers · Bloggers',
    color: '#00ff9d',
    icon: Zap,
    payout: '15% for 90 days',
    highlight: 'Payout via UPI or crypto',
    perks: [
      'Unique dashboard + tracking link within 24 hours',
      'Real-time earnings + click-through metrics',
      'Marketing kit (banners, deep links, video snippets)',
      'Monthly payouts · minimum ₹500 threshold',
    ],
  },
  {
    id: 'reseller',
    label: 'Reseller Program',
    tagline: 'Cybersecurity trainers · Agencies',
    color: '#4de0ff',
    icon: Users,
    payout: '20% wholesale · optional white-label',
    highlight: 'Bulk pricing + private client dashboard',
    perks: [
      'Wholesale ebook & course licensing',
      'White-label option (host training under your own brand)',
      'Priority queue placement on client consultations',
      'Dedicated account manager for accounts > ₹50k/mo',
    ],
  },
  {
    id: 'case_share',
    label: 'Case-Sharing',
    tagline: 'Consultation coaches · OSINT freelancers',
    color: '#c084fc',
    icon: ShieldCheck,
    payout: '40 – 60% per case',
    highlight: 'Choose the cases you take',
    perks: [
      'See open cases first — accept the ones you can help with',
      'Escrow held in wallet until case is closed',
      'No overhead — we handle client comms + payment ops',
      'Public callsign + case-count on your analyst profile',
    ],
  },
];

const CollaborateSection = () => {
  const [openTrack, setOpenTrack] = useState(null);
  return (
    <section className="relative py-16 sm:py-22 overflow-hidden" data-testid="collab-section">
      {/* Ambient */}
      <div aria-hidden className="pointer-events-none absolute -top-16 -left-20 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: '#00ff9d' }} />
      <div aria-hidden className="pointer-events-none absolute top-40 right-0 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: '#c084fc' }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-16 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: '#4de0ff' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-14">
          <div className="eh-kicker justify-center mb-3">// COLLABORATE · EARN WITH US</div>
          <h2
            className="font-black leading-[1.05]"
            style={{ fontSize: 'clamp(1.9rem, 5.5vw, 3.6rem)', fontFamily: 'Cinzel, serif' }}
          >
            <span className="block opacity-90">JOIN THE OPS.</span>
            <span
              className="block"
              style={{
                background: 'linear-gradient(90deg, #00ff9d, #4de0ff, #c084fc)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 20px rgba(0,255,157,.35))',
              }}
            >
              GET PAID FOR IT.
            </span>
          </h2>
          <p className="opacity-75 mt-4 max-w-2xl mx-auto text-[13.5px] sm:text-sm leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
            Three ways to plug your audience or skills into ErrorHacker and earn.
            No lock-in, transparent payouts, encrypted comms.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
          {TRACKS.map(t => {
            const I = t.icon;
            return (
              <div
                key={t.id}
                className="collab-tile relative rounded-2xl border-2 p-6 sm:p-7 overflow-hidden flex flex-col"
                style={{
                  borderColor: `${t.color}55`,
                  background: `linear-gradient(160deg, ${t.color}0f 0%, transparent 65%)`,
                  '--c': t.color,
                }}
                data-testid={`collab-track-${t.id}`}
              >
                <span aria-hidden className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${t.color}, transparent)` }} />

                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${t.color}1a`, border: `1px solid ${t.color}55`, color: t.color }}>
                    <I size={19} strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <div className="eh-mono text-[10px] tracking-widest opacity-70" style={{ color: t.color }}>// {t.tagline}</div>
                    <div className="font-black text-[16px] sm:text-lg leading-tight" style={{ fontFamily: 'Inter,sans-serif' }}>{t.label}</div>
                  </div>
                </div>

                <div className="mb-4 p-3 rounded-lg border" style={{ borderColor: `${t.color}44`, background: `${t.color}0d` }}>
                  <div className="eh-mono text-[9.5px] tracking-widest opacity-70 mb-0.5">PAYOUT</div>
                  <div
                    className="font-black leading-none"
                    style={{ color: t.color, fontSize: 'clamp(1.15rem, 3vw, 1.5rem)', fontFamily: 'Cinzel, serif', textShadow: `0 0 16px ${t.color}55` }}
                  >
                    {t.payout}
                  </div>
                  <div className="eh-mono text-[10px] opacity-70 mt-1">{t.highlight}</div>
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {t.perks.map(p => (
                    <li key={p} className="flex items-start gap-2 text-[12.5px] leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
                      <CheckCircle2 size={13} className="mt-0.5 shrink-0" style={{ color: t.color }} strokeWidth={2} />
                      <span className="opacity-90">{p}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setOpenTrack(t)}
                  className="w-full flex items-center justify-center gap-1.5 py-3 rounded-lg font-black text-[11px] tracking-widest transition-all hover:brightness-110"
                  style={{ background: t.color, color: '#04120a', boxShadow: `0 10px 26px -10px ${t.color}80` }}
                  data-testid={`collab-apply-${t.id}`}
                >
                  APPLY NOW <ArrowRight size={12} strokeWidth={2.5} />
                </button>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-8 opacity-60 eh-mono text-[10.5px] tracking-widest">
          <Sparkles size={11} className="inline mr-1 text-[#ffd34d]" />
          NEW COLLABS ONBOARD EVERY MONDAY · CURRENTLY ACCEPTING APPLICATIONS
        </div>
      </div>

      <style>{`
        .collab-tile { transition: transform .35s cubic-bezier(.2,.9,.3,1), border-color .25s, box-shadow .35s; }
        .collab-tile:hover { transform: translateY(-4px); border-color: var(--c) !important; box-shadow: 0 30px 70px -25px color-mix(in srgb, var(--c) 60%, transparent); }
      `}</style>

      {openTrack && <CollabModal track={openTrack} onClose={() => setOpenTrack(null)} />}
    </section>
  );
};

// -------------------------------------------------------------------------
// Apply modal — same submit flow for all 3 tracks. Uses api.collabApply().
// -------------------------------------------------------------------------
const CollabModal = ({ track, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', channel: '', pitch: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.channel.trim() || form.pitch.trim().length < 10) {
      toast.error('Fill in every field — pitch needs at least a sentence.');
      return;
    }
    setBusy(true);
    try {
      await api.collabApply({ ...form, track: track.id });
      setDone(true);
    } catch (e) {
      toast.error(e.message || 'Submission failed — try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md grid place-items-center p-4" onClick={onClose} data-testid="collab-modal">
      <div
        className="w-full max-w-lg rounded-2xl border-2 p-5 sm:p-6 relative"
        style={{ borderColor: `${track.color}55`, background: 'linear-gradient(180deg, #060907, #030403)', boxShadow: `0 40px 80px -20px ${track.color}45` }}
        onClick={e => e.stopPropagation()}
      >
        <span aria-hidden className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${track.color}, transparent)` }} />
        <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-lg grid place-items-center border transition-colors" style={{ borderColor: 'var(--eh-border)', color: 'rgba(255,255,255,.65)' }} aria-label="Close">
          <X size={13} />
        </button>

        {done ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full grid place-items-center" style={{ background: `${track.color}18`, color: track.color, border: `1px solid ${track.color}55` }}>
              <CheckCircle2 size={26} strokeWidth={2} />
            </div>
            <div className="eh-display text-xl font-black mb-2" style={{ color: track.color }}>Application received</div>
            <p className="text-sm opacity-80 leading-relaxed max-w-sm mx-auto" style={{ fontFamily: 'Inter,sans-serif' }}>
              We review new {track.label.toLowerCase()} applications every Monday. You&apos;ll get a DM from an operator within 5 working days.
            </p>
            <button onClick={onClose} className="mt-6 eh-btn-ghost text-xs" data-testid="collab-modal-close">CLOSE</button>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <div className="eh-kicker mb-1" style={{ color: track.color }}>// APPLY · {track.label.toUpperCase()}</div>
              <div className="eh-display text-lg sm:text-xl font-black">Tell us about you.</div>
              <p className="text-[12.5px] opacity-70 mt-1" style={{ fontFamily: 'Inter,sans-serif' }}>Fills below → operator reviews → we DM you within 5 working days.</p>
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="collab-form">
              <Field label="Your name">
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="How do we call you?" className="collab-input" data-testid="collab-name" required />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Email">
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@domain.com" className="collab-input" data-testid="collab-email" required />
                </Field>
                <Field label="Your channel / handle">
                  <input value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })} placeholder={track.id === 'reseller' ? 'yourpanel.com' : '@youtube_handle'} className="collab-input" data-testid="collab-channel" required />
                </Field>
              </div>
              <Field label="30-second pitch">
                <textarea
                  value={form.pitch}
                  onChange={e => setForm({ ...form, pitch: e.target.value })}
                  rows={4}
                  placeholder={{
                    affiliate: 'What do you make? Which audience? Rough monthly reach?',
                    reseller: 'What kind of clients? Rough monthly training / audit spend?',
                    case_share: 'What ops have you worked before? Which platforms are you strongest on?',
                  }[track.id]}
                  className="collab-input resize-none"
                  data-testid="collab-pitch"
                  required
                />
              </Field>

              <button
                type="submit"
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-black text-[12px] tracking-widest transition-all disabled:opacity-50 hover:brightness-110"
                style={{ background: track.color, color: '#04120a', boxShadow: `0 10px 26px -10px ${track.color}90` }}
                data-testid="collab-submit"
              >
                {busy ? <><Loader2 size={13} className="animate-spin" /> SUBMITTING…</> : <><Send size={12} strokeWidth={2.5} /> SEND APPLICATION</>}
              </button>
            </form>
          </>
        )}
      </div>

      <style>{`
        .collab-input {
          width: 100%;
          padding: 10px 12px;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,.12);
          background: rgba(0,0,0,.35);
          font-family: Inter, sans-serif;
          font-size: 13px;
          color: #fff;
          outline: none;
          transition: border-color .15s;
        }
        .collab-input:focus { border-color: ${track.color}; box-shadow: 0 0 0 3px ${track.color}22; }
        .collab-input::placeholder { color: rgba(255,255,255,.35); }
      `}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="eh-mono text-[10px] tracking-widest opacity-65 block mb-1.5">{label}</span>
    {children}
  </label>
);

export default CollaborateSection;
